import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Minus, UserPlus, Save, Check, Users } from 'lucide-react';
import { toast } from 'sonner';
import { 
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Bemanning, TalentBehovItem, produksjonAPI, talentAPI, Talent } from '../../services/api';
import TalentDeleteConfirmationDialog from './TalentDeleteConfirmationDialog';
import SectionTitle from './SectionTitle';
import DraftBemanningDrawer from './DraftBemanningDrawer';
import { useDraftBemanning } from '../../hooks/useDraftBemanning';
import { TaskCard } from './TaskCard';
import { CategoryHeader } from './CategoryHeader';
import { useTalentHierarchy, type HierarchyNode } from '../../hooks/useTalentHierarchy';
import { getTaskStatus, getCategoryStatus } from '../../lib/statusHelpers';
import { useTalentBehovCRUD } from './talent-manager/hooks/useTalentBehovCRUD';

interface Props {
  produksjonId: number;
  talentBehov: TalentBehovItem[];
  bemanning: Bemanning[];
  onChanged: () => void;
  refreshBemanning?: () => Promise<void>;
  refreshTalentBehov?: () => Promise<void>;
  readOnly?: boolean;
  /** Kalles med true når bemanningsskuffen er åpen og festet, ellers false (også ved unmount). */
  onDrawerPinnedChange?: (pinned: boolean) => void;
}

const ResourcePlanning: React.FC<Props> = ({ 
  produksjonId, 
  talentBehov, 
  bemanning, 
  onChanged, 
  refreshBemanning, 
  refreshTalentBehov,
  readOnly = false,
  onDrawerPinnedChange,
}) => {
  // Bygg hierarkisk tre-struktur fra talent_kategori_sti (via custom hook)
  const hierarchy = useTalentHierarchy(talentBehov);
  

  const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});

  const [addOpen, setAddOpen] = useState(false);
  const [allTalenter, setAllTalenter] = useState<Talent[]>([]);
  const [valgtTalent, setValgtTalent] = useState<Talent | null>(null);
  const [nyAntall, setNyAntall] = useState(1);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [talentToDelete, setTalentToDelete] = useState<TalentBehovItem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerPinned, setDrawerPinned] = useState(false);
  const [drawerTalent, setDrawerTalent] = useState<{
    talent_navn: string;
    talent_kategori: string;
    antall: number;
    antallFylt: number;
  } | null>(null);

  // Gi forelderen beskjed når festet skuff skal skyve innholdet
  const drawerOccupiesSpace = drawerPinned && drawerOpen;
  useEffect(() => {
    onDrawerPinnedChange?.(drawerOccupiesSpace);
  }, [drawerOccupiesSpace, onDrawerPinnedChange]);
  useEffect(() => {
    return () => onDrawerPinnedChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Use new local draft hook instead of global Context
  const draft = useDraftBemanning();

  // Use CRUD hook for talent-behov operations (extracted from monolith)
  const talentCRUD = useTalentBehovCRUD({
    produksjonId,
    onSuccess: refreshTalentBehov,
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await talentAPI.getAll();
        setAllTalenter(res.talenter);
      } catch {
        setAllTalenter([]);
      }
    })();
  }, []);

  // Sett originalList når bemanning endres (kun ved første lasting eller når antall personer endrer seg)
  useEffect(() => {
    if (bemanning && bemanning.length >= 0) {
      draft.setOriginalList(bemanning as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bemanning?.length]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };


  // Status helpers - nå importert fra lib/statusHelpers.ts


  const updateTaskCount = async (task: TalentBehovItem, delta: number) => {
    const next = Math.max(0, task.antall + delta);
    
    // Hvis antall blir 0, vis bekreftelsesdialog
    if (next === 0) {
      setTalentToDelete(task);
      setDeleteDialogOpen(true);
      return;
    }
    
    // Bruk CRUD hook for bedre separasjon av business logic
    await talentCRUD.adjustCount(task, delta);
  };

  const handlePublishAll = async () => {
    if (!draft.hasUnsavedChanges) return;
    
    try {
      const added = (draft.pendingChanges.added as any[]).map((p: any) => ({
        id: p.id,
        talent_navn: p.talent_navn,
        talent_kategori_sti: p.talent_kategori_sti,
        notater: p.notater || null,
        status: p.status || 'planlagt'
      }));
      const removed = draft.pendingChanges.removed.map((p: any) => ({ 
        id: p.person_id || p.id, 
        talent_navn: p.talent_navn 
      }));
      const modified = (draft.pendingChanges.modified as any[]).map((p: any) => ({
        id: p.id,
        // Identifiserer entydig hvilken bemanning-rad som skal endres når
        // personen har flere roller i produksjonen (se draft/service.js)
        bemanningId: p.bemanningId,
        originalTalentNavn: p.originalTalentNavn,
        talent_navn: p.talent_navn,
        talent_kategori_sti: p.talent_kategori_sti,
        notater: p.notater || null,
        status: p.status || 'planlagt'
      }));

      await produksjonAPI.publishDraftChanges(produksjonId, {
        added,
        removed,
        modified,
      });
      
      draft.resetChanges();
      draft.exitDraftMode();
      if (refreshBemanning) await refreshBemanning();
    } catch (e) {
      console.error('Feil ved publisering av endringer:', e);
      toast.error('Kunne ikke publisere endringer');
    }
  };

  const handleAdd = async () => {
    if (!valgtTalent) return;
    try {
      // Bruk CRUD hook for bedre error handling
      await talentCRUD.addTalentBehov(
        valgtTalent.navn,
        valgtTalent.kategori_navn || '',
        Math.max(1, nyAntall)
      );
      setAddOpen(false);
      setValgtTalent(null);
      setNyAntall(1);
    } catch (error: any) {
      // Error håndteres i hooken, men vi viser også toast
      if (talentCRUD.error) {
        toast.error(talentCRUD.error);
      } else {
        toast.error('Kunne ikke legge til talent-behov. Prøv igjen.');
      }
      console.error('Feil ved adding talent-behov:', error);
    }
  };

  const handleConfirmDelete = async () => {
    if (!talentToDelete) return;
    
    setDeleting(true);
    try {
      // Bruk CRUD hook for konsistens
      await talentCRUD.removeTalentBehov(talentToDelete.id);
      setDeleteDialogOpen(false);
      setTalentToDelete(null);
    } catch (error) {
      console.error('Feil ved sletting av talent:', error);
      if (talentCRUD.error) {
        toast.error(talentCRUD.error);
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setTalentToDelete(null);
  };

  // Unike tellere for status-chip
  const uniqueAddedCount = useMemo(() => new Set(draft.pendingChanges.added.map(p => p.id)).size, [draft.pendingChanges.added]);
  const uniqueRemovedCount = useMemo(() => new Set(draft.pendingChanges.removed.map(p => p.id)).size, [draft.pendingChanges.removed]);

  const renderTask = (task: TalentBehovItem, categoryId: string) => {
    const peopleForTask = bemanning.filter(b => b.talent_navn === task.talent_navn);
    const draftPeopleForTask = (draft.pendingChanges.added as any[]).filter((p: any) => p.talent_navn === task.talent_navn);
    const removedPeopleIds = new Set(draft.pendingChanges.removed.map(p => p.id));

    return (
      <TaskCard
        key={task.id}
        task={task}
        people={peopleForTask}
        draftPeople={draftPeopleForTask}
        removedPeopleIds={removedPeopleIds}
        onUpdateCount={(delta) => updateTaskCount(task, delta)}
        onAssignPerson={() => {
          setDrawerTalent({
            talent_navn: task.talent_navn,
            talent_kategori: task.talent_kategori,
            antall: task.antall,
            antallFylt: peopleForTask.length,
          });
          setDrawerOpen(true);
        }}
        onRemovePerson={
          readOnly
            ? undefined
            : (id) => {
                console.log('🗑️ Fjerner person:', id);
                draft.removePerson(id);
              }
        }
        readOnly={readOnly}
      />
    );
  };

  // Rekursiv rendering av hierarkisk node med Collapsible
  const renderHierarchyNode = (node: HierarchyNode, key: string, depth: number): React.ReactNode => {
    const isExpanded = expandedCategories[node.path] ?? false;
    
    // Samle alle talents fra denne noden og alle sub-noder rekursivt
    const getAllTalents = (n: HierarchyNode): TalentBehovItem[] => {
      const talents = [...n.talents];
      n.children.forEach(child => {
        talents.push(...getAllTalents(child));
      });
      return talents;
    };
    
    const allTalentsInNode = getAllTalents(node);
    const status = getCategoryStatus(allTalentsInNode, bemanning);
    const hasChildren = node.children.size > 0;
    const hasTalents = node.talents.length > 0;

    return (
      <div key={key} style={{ marginLeft: depth > 0 ? '2rem' : '0' }}>
        <Collapsible open={isExpanded} onOpenChange={() => toggleCategory(node.path)}>
          <div className="bg-card rounded-xl shadow-md border-2 overflow-hidden hover:shadow-lg transition-all mb-4">
            {/* Node Header */}
            <CollapsibleTrigger asChild>
              <CategoryHeader
                name={node.name}
                stats={status}
                depth={depth}
                isExpanded={isExpanded}
                onToggle={() => toggleCategory(node.path)}
                hasChildren={hasChildren}
                hasTalents={hasTalents}
              />
            </CollapsibleTrigger>

            {/* Node Content */}
            <CollapsibleContent>
              <div className="p-3 space-y-3">
                {/* Render sub-noder rekursivt */}
                {hasChildren && (
                  <div className="space-y-3">
                    {Array.from(node.children.entries()).map(([childKey, childNode]) =>
                      renderHierarchyNode(childNode, `${key}-${childKey}`, depth + 1)
                    )}
                  </div>
                )}
                
                {/* Render talents for denne noden */}
                {hasTalents && (
                  <div className={hasChildren ? 'mt-3 pt-3' : ''}>
                    <Separator className="mb-3" />
                    <div className="space-y-3">
                      {node.talents.map((task) => renderTask(task, node.path))}
                    </div>
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>
      </div>
    );
  };


  return (
    <div className="max-w-7xl mx-auto px-4 mb-6">
      <div className="bg-white rounded-xl shadow-md p-3 sm:p-6">
        {/* Header - Mobile: Stack vertikalt, Desktop: Horisontal */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <SectionTitle icon={Users}>Bemanning</SectionTitle>
          
          {!readOnly && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
              <span
                role="status"
                aria-live="polite"
                className="flex items-center gap-2 text-sm text-muted-foreground whitespace-nowrap"
              >
                {draft.hasUnsavedChanges ? (
                  <>
                    <span className="h-2 w-2 rounded-full bg-status-draft" aria-hidden="true" />
                    Ulagret: {uniqueAddedCount} lagt til, {uniqueRemovedCount} fjernet
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4" aria-hidden="true" />
                    Alt er lagret
                  </>
                )}
              </span>
              <Button
                onClick={() => setAddOpen(true)}
                variant="outline"
                size="sm"
                className="gap-2 w-full sm:w-auto"
              >
                <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="text-xs sm:text-sm">Legg til talent-behov</span>
              </Button>
              {draft.hasUnsavedChanges && (
                <Button
                  onClick={handlePublishAll}
                  variant="default"
                  size="sm"
                  className="gap-2 w-full sm:w-auto"
                >
                  <Save className="h-4 w-4" />
                  <span className="text-xs sm:text-sm">Lagre endringer</span>
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Categories - Hierarkisk 3-nivå rendering */}
        <div className="space-y-4">
          {Array.from(hierarchy.entries()).map(([rootKey, rootNode]) => 
            renderHierarchyNode(rootNode, rootKey, 0)
          )}
        </div>

        {/* Vis tom state hvis ingen talent-behov */}
        {hierarchy.size === 0 && (
          <div className="bg-card rounded-xl shadow-md border-2 border-dashed p-12 text-center">
            <div className="text-muted-foreground mb-4">
              <UserPlus size={64} className="mx-auto" />
            </div>
            <h3 className="text-xl font-bold text-foreground mb-2">Ingen talent-behov lagt til ennå</h3>
            {!readOnly && (
              <>
                <p className="text-muted-foreground mb-6">Start med å legge til talent-behov for denne produksjonen</p>
                <Button 
                  onClick={() => setAddOpen(true)}
                  size="lg"
                  className="gap-2 mx-auto"
                >
                  <Plus className="h-5 w-5" />
                  Legg til første talent-behov
                </Button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Add Talent Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Legg til talent-behov</DialogTitle>
            <DialogDescription>
              Velg et talent og angi hvor mange personer som trengs
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Talent Select */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Velg talent fra hierarkiet
              </label>
              <Select 
                value={valgtTalent?.navn || ''} 
                onValueChange={(value) => {
                  const talent = allTalenter.find(t => t.navn === value);
                  setValgtTalent(talent || null);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Velg talent..." />
                </SelectTrigger>
                <SelectContent>
                  {allTalenter
                    .filter(t => !talentBehov.some(b => b.talent_navn === t.navn))
                    .map((t) => (
                      <SelectItem key={t.id} value={t.navn}>
                        {t.navn} • {t.kategori_navn || ''}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {allTalenter.filter(t => !talentBehov.some(b => b.talent_navn === t.navn)).length === 0 && (
                <p className="text-sm text-muted-foreground italic">
                  Ingen flere talents tilgjengelig (alle er allerede lagt til)
                </p>
              )}
            </div>

            {/* Antall personer */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Antall personer:
              </label>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline"
                  size="icon"
                  onClick={() => setNyAntall(Math.max(1, nyAntall - 1))}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="px-4 font-bold text-lg">{nyAntall}</span>
                <Button 
                  variant="outline"
                  size="icon"
                  onClick={() => setNyAntall(nyAntall + 1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <p className="text-sm text-muted-foreground italic">
              Dette legger til et talent-behov som du deretter kan bemanne med personer
            </p>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline"
              onClick={() => setAddOpen(false)}
            >
              Avbryt
            </Button>
            <Button 
              onClick={handleAdd}
              disabled={!valgtTalent}
            >
              Legg til
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TalentDeleteConfirmationDialog
        open={deleteDialogOpen}
        talentName={talentToDelete?.talent_navn || ''}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={deleting}
      />

      <DraftBemanningDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        produksjonId={produksjonId}
        selectedTalent={drawerTalent}
        draft={draft}
        onSuccess={async () => {
          if (refreshBemanning) {
            await refreshBemanning();
          }
        }}
        onPinToggle={(p) => setDrawerPinned(p)}
        isPinned={drawerPinned}
      />
    </div>
  );
};

export default ResourcePlanning;

